import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  VStack,
  Text,
  Icon,
} from "@chakra-ui/react";
import { FiUser, FiLock } from "react-icons/fi";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "./config";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/api/admin/auth/admin/login`, {
        email,
        password,
      });

      if (response.data.status) {
        // Store the token in localStorage
        localStorage.setItem("adminToken", response.data.data.token);
        toast.success("Login successful!");
        navigate("/dashboard");
      } else {
        toast.error(response.data.message || "Login failed");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="gray.900"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
    >
      {/* Background gradient */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 50% 0%, rgba(33, 150, 243, 0.1) 0%, transparent 50%)"
        pointerEvents="none"
      />

      <Container maxW="md" px={6}>
        <Box
          bg="gray.800"
          border="1px solid"
          borderColor="gray.700"
          borderRadius="16px"
          p={8}
          backdropFilter="blur(10px)"
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        >
          <VStack spacing={6} align="stretch">
            {/* Logo/Brand */}
            <Box textAlign="center" mb={2}>
              <Heading
                size="xl"
                color="white"
                fontWeight="700"
                letterSpacing="-0.025em"
              >
                PerpX
              </Heading>
              <Text color="gray.400" fontSize="md" mt={1}>
                Admin Dashboard
              </Text>
            </Box>

            {/* Welcome Text */}
            <Box textAlign="center" mb={2}>
              <Heading size="md" color="gray.200" fontWeight="600" mb={2}>
                Welcome back
              </Heading>
              <Text color="gray.400" fontSize="sm">
                Sign in to access your admin panel
              </Text>
            </Box>

            <form onSubmit={handleSubmit}>
              <Stack spacing={5}>
                <FormControl id="email" isRequired>
                  <FormLabel color="gray.300" fontWeight="500">
                    Email address
                  </FormLabel>
                  <Box position="relative">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@perpx.com"
                      pl={10}
                      h={12}
                      bg="gray.800"
                      border="1px solid"
                      borderColor="gray.600"
                      borderRadius="8px"
                      _hover={{
                        borderColor: "gray.500",
                      }}
                      _focus={{
                        borderColor: "brand.500",
                        boxShadow: "0 0 0 1px #2196f3",
                      }}
                    />
                    <Icon
                      as={FiUser}
                      position="absolute"
                      left={3}
                      top="50%"
                      transform="translateY(-50%)"
                      color="gray.500"
                      w={4}
                      h={4}
                    />
                  </Box>
                </FormControl>

                <FormControl id="password" isRequired>
                  <FormLabel color="gray.300" fontWeight="500">
                    Password
                  </FormLabel>
                  <Box position="relative">
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      pl={10}
                      h={12}
                      bg="gray.800"
                      border="1px solid"
                      borderColor="gray.600"
                      borderRadius="8px"
                      _hover={{
                        borderColor: "gray.500",
                      }}
                      _focus={{
                        borderColor: "brand.500",
                        boxShadow: "0 0 0 1px #2196f3",
                      }}
                    />
                    <Icon
                      as={FiLock}
                      position="absolute"
                      left={3}
                      top="50%"
                      transform="translateY(-50%)"
                      color="gray.500"
                      w={4}
                      h={4}
                    />
                  </Box>
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  h={12}
                  bg="brand.600"
                  color="white"
                  fontWeight="600"
                  borderRadius="8px"
                  mt={6}
                  isLoading={loading}
                  loadingText="Signing In..."
                  _hover={{
                    bg: "brand.700",
                    transform: "translateY(-1px)",
                    boxShadow: "lg",
                  }}
                  _active={{
                    bg: "brand.800",
                    transform: "translateY(0)",
                  }}
                >
                  Sign In
                </Button>
              </Stack>
            </form>

            {/* Footer */}
            <Box textAlign="center" pt={4}>
              <Text color="gray.500" fontSize="xs">
                © 2025 PerpX. All rights reserved.
              </Text>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}

export default Login;
